import type { FC, ReactNode } from 'react'
import type { Service } from '@/types'

import { useEffect, useId, useState } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/kit/button'
import { Sheet } from '@/components/kit/sheet'
import { Field, Input, Select } from '@/components/kit/field'
import { DATE_PRESETS } from '@/lib/carwash/ranges'

export type VehicleFilters = {
    date: string
    service: string
}

interface VehicleFilterSheetProps {
    open: boolean
    onClose: () => void
    value: VehicleFilters
    onApply: (value: VehicleFilters) => void
    services: Service[]
    hasCustom: boolean
}

export const VehicleFilterSheet: FC<VehicleFilterSheetProps> = ({
    open,
    onClose,
    value,
    onApply,
    services,
    hasCustom
}): ReactNode => {
    const dateId = useId()
    const serviceId = useId()
    const [draft, setDraft] = useState<VehicleFilters>(value)

    useEffect(() => {
        if (open) setDraft({ date: value.date, service: value.service })
    }, [open, value.date, value.service])

    const pickedDay = /^\d{4}-\d{2}-\d{2}$/.test(draft.date) ? draft.date : ''

    return (
        <Sheet
            open={open}
            onClose={onClose}
            title='Filter vehicles'
            size='sm'
            footer={(
                <div className='grid grid-cols-2 gap-3'>
                    <Button
                        variant='secondary'
                        size='lg'
                        onClick={() => {
                            onApply({ date: 'today', service: 'all' })
                            onClose()
                        }}
                    >
                        Reset
                    </Button>

                    <Button
                        size='lg'
                        onClick={() => {
                            onApply(draft)
                            onClose()
                        }}
                    >
                        Show results
                    </Button>
                </div>
            )}
        >
            <div className='space-y-5'>
                <fieldset>
                    <legend className='mb-2 text-sm font-semibold text-foreground'>
                        Date
                    </legend>

                    <div className='flex flex-wrap gap-2'>
                        {DATE_PRESETS.map(preset => {
                            const active = draft.date === preset.value

                            return (
                                <button
                                    key={preset.value}
                                    type='button'
                                    aria-pressed={active}
                                    onClick={() => setDraft(current => ({ ...current, date: preset.value }))}
                                    className={cn(
                                        'h-10 rounded-full border px-4 text-sm font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                                        active ? 'border-primary bg-accent text-accent-foreground' : 'border-border bg-card text-slate-700 hover:bg-slate-50'
                                    )}
                                >
                                    {preset.label}
                                </button>
                            )
                        })}
                    </div>

                    <Field label='Or pick a day' htmlFor={dateId} className='mt-4'>
                        <Input
                            id={dateId}
                            type='date'
                            value={pickedDay}
                            onChange={event => setDraft(current => ({ ...current, date: event.target.value || 'today' }))}
                        />
                    </Field>
                </fieldset>

                <Field label='Service' htmlFor={serviceId}>
                    <Select
                        id={serviceId}
                        value={draft.service}
                        onChange={event => setDraft(current => ({ ...current, service: event.target.value }))}
                    >
                        <option value='all'>All services</option>
                        {services.map(service => (
                            <option key={service.id} value={service.id}>
                                {service.active ? service.name : `${service.name} (inactive)`}
                            </option>
                        ))}
                        {hasCustom && <option value='custom'>Custom services</option>}
                    </Select>
                </Field>
            </div>
        </Sheet>
    )
}
